import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux"
import debounce from 'lodash.debounce';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Button, CloseButton, Input, InputGroup, InputLeftElement, InputRightElement, useDisclosure } from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import Modal from "components/modal/Modal";
import { FaTrashAlt } from "react-icons/fa";
import TooltipComponent from "components/tooltip";
import { FiSearch } from "react-icons/fi";
import ButtonPrimary from "components/button/ButtonPrimary";
import { SET_TO_DEFAULT_RESPONSE, getByPage, anulacion } from "./../../../store/features/slices/ventaSlice";
import { message as alerta } from "utils/Funciones";
import { uriSerialized } from "utils/uriSerialized";
import SwitchToggle from "components/fields/SwitchToggle";
import Spinner from "components/spinner/Spinner";
import { MdBlock, MdDescription } from "react-icons/md";
import TextField from "components/fields/TextField";
import TablePagination from "./table/TablePagination";
import TableCartItemsDetalles from "./table/TableCartItemsDetalles";

const Ventas = () => {
    const dispatch = useDispatch();
    const { userToken } = useSelector((state) => state.userReducer);
    const {
        dataByPage,
        totalPage,
        totalRow,
        isLoading,
        responseSuccessAnular
    } = useSelector((state) => state.ventaReducer);

    const detalles = useDisclosure()
    const anular = useDisclosure()
    const [searchTerm, setSearchTerm] = useState('');
    const [stateanulacion, setStateAnulacion] = useState({ open: false, _id: "", motivo: "", fileName: "", restock: true });

    const [stateDetalles, setStateDetalles] = useState(null)

    function handleDetalles(row) {
        setStateDetalles(row)
        detalles.onOpen()
    }

    const handleAnulacion = (row) => {
        setStateAnulacion({ ...stateanulacion, _id: row._id, fileName: row.sunat.fileName })
        anular.onOpen()
    }

    const handleSendAnulacion = (e) => {
        e.preventDefault();
        if (stateanulacion.motivo.trim().length < 1) {
            return alerta('Ingresa el motivo de la anulación', 'error', 5)
        }
        const obj = {
            _id: stateanulacion._id,
            motivo: stateanulacion.motivo,
            restock: stateanulacion.restock
        }
        const formData = new FormData();
        formData.append('data', JSON.stringify(obj));
        dispatch(anulacion({ formData: formData, token: userToken }));

    }

    useEffect(() => {
        if (responseSuccessAnular) {
            alerta(responseSuccessAnular.message, 'success', 5)
            anular.onClose()
        }
        dispatch(SET_TO_DEFAULT_RESPONSE())
    }, [responseSuccessAnular])

    const fetchData = useCallback(
        (pageSize, pageIndex, search, order) => {
            const queryOptions = {
                page: pageIndex,
                limit: pageSize,
                search: search,
                order: order,
            }
            const query = queryOptions ? "?" + uriSerialized(queryOptions) : ""
            dispatch(getByPage({ query: query, token: userToken }))
        },
        []
    )

    const onChangeSearchTerm = e => {
        setSearchTerm(e.target.value);
    };

    const onChangeSearchTermClear = e => {
        setSearchTerm("");
        document.getElementById("search").value = "";
    };

    const debounceSearchTerm = useMemo(() => debounce(onChangeSearchTerm, 700), []);

    return (
        <div className="mt-4 md:mt-0">
            <div className="sticky z-30 w-full top-44 md:top-28 flex items-center justify-end bg-white dark:!bg-navy-800 p-2 rounded-xl shadow-xl shadow-gray-200 dark:shadow-none">
                <div className="relative flex h-[61px] w-[calc(100%-55px)] flex-grow items-center gap-2 rounded-full bg-white px-0 py-2 dark:!bg-navy-800">
                    <InputGroup className="!w-full md:!w-[300px]">
                        <InputLeftElement pointerEvents="none" className="!mt-1 !ml-1">
                            <FiSearch size={24} className="text-navy-500 dark:text-navy-300" />
                        </InputLeftElement>
                        <Input size='lg' autoFocus id="search" onChange={debounceSearchTerm} type="text" placeholder={`Buscar ${totalRow} ventas...`} className="flex w-full items-center justify-center !rounded-xl border !bg-lightPrimary 
            dark:!bg-navy-900 !text-sm !ring-none focus:!ring-brand-500 
            focus:!border-brand-500 dark:focus:!ring-brand-400 dark:focus:!border-brand-400 
            !outline-none !border-gray-200 dark:!border-white/10 dark:text-white" />
                        <InputRightElement className="!mt-1">
                            <CloseButton onClick={onChangeSearchTermClear} size='md' className={`mr-2 ${searchTerm.trim().length > 0 ? 'scale-100' : 'scale-0'} transition-all duration-500 text-navy-500 dark:text-navy-300`} />
                        </InputRightElement>
                    </InputGroup>
                </div>
            </div>
            <div className="mt-3 md:mt-8">

                {/** ANULACION */}

                <Modal isOpen={anular.isOpen} onClose={anular.onClose} headerTitle='Anular venta'>
                    <div className="px-[5px] md:px-[10px] pt-[20px] pb-[20px] max-w-[1368px] flex flex-col !z-[1004]">
                        <form className="w-full" onSubmit={handleSendAnulacion}>
                            <div className="flex flex-wrap">
                                <Alert
                                    status='info'
                                    variant='subtle'
                                    flexDirection='column'
                                    alignItems='center'
                                    justifyContent='center'
                                    textAlign='center'
                                    height='200px'
                                >
                                    <AlertIcon boxSize='40px' mr={0} />
                                    <AlertTitle mt={4} mb={1} fontSize='lg'>
                                        Anular venta
                                    </AlertTitle>
                                    <AlertDescription maxWidth='sm'>
                                        Estás anulando la venta <b>{stateanulacion.fileName}</b> por favor ten en cuenta si los productos estan ingresando otra vez al inventario o se están descartando para reponerlos despues!
                                    </AlertDescription>
                                </Alert>
                                <SwitchToggle
                                    extra="w-full mb-3 p-2"
                                    label="Están regresando los productos al inventario?"
                                    id="restock"
                                    onClick={(e) => setStateAnulacion({ ...stateanulacion, restock: !stateanulacion.restock })}
                                    value={stateanulacion.restock}
                                />
                                <TextField
                                    autoFocus={true}
                                    required={true}
                                    variant="input"
                                    extra="w-full md:w-6/12 mb-3 p-2"
                                    label="Motivo"
                                    placeholder="Motivo"
                                    id="motivo"
                                    type="text"
                                    name="motivo"
                                    onChange={(e) => setStateAnulacion({ ...stateanulacion, motivo: e.target.value })}
                                    value={stateanulacion.motivo}
                                />

                                <ButtonPrimary isLoading={isLoading} icon={<MdBlock size={28} />}>
                                    Anular
                                </ButtonPrimary>
                            </div>
                        </form>
                    </div>
                </Modal>

                {/** DETALLES */}
                <Modal isOpen={detalles.isOpen} onClose={detalles.onClose} headerTitle='Detalles de la venta'>
                    {stateDetalles !== null &&
                        <div className="px-[5px] md:px-[10px] pt-[20px] pb-[20px] max-w-[1368px] flex flex-col !z-[1004]">
                            <form className="w-full">
                                {stateDetalles.sunat.status === "BAJA" &&
                                    <Alert
                                        variant='subtle'
                                        flexDirection='column'
                                        alignItems='center'
                                        justifyContent='center'
                                        textAlign='center'
                                        height='auto'
                                        status='error'
                                    >
                                        <AlertIcon boxSize='40px' mr={0} />
                                        <AlertTitle mt={4} mb={1} fontSize='lg'>
                                            Esta venta con código de comprobante <b>{stateDetalles.sunat.fileName}</b> está de BAJA o ANULADA
                                        </AlertTitle>
                                        <AlertDescription maxWidth='sm' fontSize={17}>
                                            Motivo : <b>{stateDetalles.anulacion.motivo}</b>, los productos {stateDetalles.anulacion.restock ? 'SI' : 'NO'} fueron restablecidos al inventario.
                                        </AlertDescription>
                                    </Alert>
                                }
                                <div className="flex flex-wrap">
                                    <InputField
                                        variant="input"
                                        iconleft={stateDetalles === null && <Spinner size="w-[25px] h-[25px]" />}
                                        disabled={true}
                                        extra="w-full md:w-4/12 mb-3 p-2"
                                        label="Tipo de comprobante"
                                        placeholder=""
                                        id="input"
                                        type="text"
                                        name="input"
                                        value={stateDetalles.sunat.type === "01" ? "FACTURA" : stateDetalles.sunat.type === "03" ? "BOLETA DE VENTA" : "NOTA DE VENTA"}
                                    />
                                    <InputField
                                        variant="input"
                                        iconleft={stateDetalles === null && <Spinner size="w-[25px] h-[25px]" />}
                                        disabled={true}
                                        extra="w-full md:w-4/12 mb-3 p-2"
                                        label="Tipo de documento"
                                        placeholder=""
                                        id="input"
                                        type="text"
                                        name="input"
                                        value={stateDetalles.cliente.tipodocumento}
                                    />
                                    <InputField
                                        variant="input"
                                        iconleft={stateDetalles === null && <Spinner size="w-[25px] h-[25px]" />}
                                        disabled={true}
                                        extra="w-full md:w-4/12 mb-3 p-2"
                                        label="Numero de documento"
                                        placeholder=""
                                        id="input"
                                        type="text"
                                        name="input"
                                        value={stateDetalles.cliente.dniruc}
                                    />
                                    <InputField
                                        variant="input"
                                        iconleft={stateDetalles === null && <Spinner size="w-[25px] h-[25px]" />}
                                        disabled={true}
                                        extra="w-full md:w-4/12 mb-3 p-2"
                                        label="Nombre completo"
                                        placeholder=""
                                        id="input"
                                        type="text"
                                        name="input"
                                        value={stateDetalles.cliente.tipodocumento === "RUC" ? stateDetalles.cliente.dataruc.razonsocial : stateDetalles.cliente.datadni.nombrecompleto}
                                    />
                                    <InputField
                                        variant="input"
                                        iconleft={stateDetalles === null && <Spinner size="w-[25px] h-[25px]" />}
                                        disabled={true}
                                        extra="w-full md:w-4/12 mb-3 p-2"
                                        label="Dirección"
                                        placeholder=""
                                        id="input"
                                        type="text"
                                        name="input"
                                        value={stateDetalles.cliente.tipodocumento === "RUC" ? stateDetalles.cliente.dataruc.direccion : stateDetalles.cliente.datadni.direccion}
                                    />
                                    <div className="w-full grid grid-cols-1 mt-3">
                                        <TableCartItemsDetalles
                                            tableData={stateDetalles}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    }
                </Modal>

                <div className="mt-5 grid h-full grid-cols-1">
                    <TablePagination
                        searchTerm={searchTerm}
                        tableData={dataByPage}
                        fetchData={fetchData}
                        loading={isLoading}
                        pageCount={totalPage}
                        totalRow={totalRow}
                        actions={{
                            onView: (item) => handleDetalles(item),
                            onAnular: (item) => handleAnulacion(item)
                        }}
                    />
                </div>
            </div>
        </div >

    );
};

export default Ventas;
